import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { CaretDownFill, Check2, Pencil, Trash } from "react-bootstrap-icons";
import { useListsContext } from "../ListsContext";
import {
  DeleteIconButton,
  DoneCount,
  IconButton,
  ListMenu,
  ListOption,
  ListOptionName,
  ListOptionText,
  OptionCheck,
  SelectAnchor,
  SelectChevron,
  SelectShell,
  SelectedName,
  SelectRow,
} from "../Lists.styled";
import { ConfirmModal } from "./ConfirmModal";
import { ListNameModal } from "./ListNameModal";

// The dropdown doubles as the selected list's name, with that list's progress on the
// line beneath it. Rename and delete sit beside it as icon buttons.
//
// The menu is drawn here rather than left to a native select: iOS Safari opens a native
// select as a small system bubble that no page styling can reach.
export function ListSelect() {
  const {
    lists,
    itemsByList,
    selectedListId,
    selectList,
    renameList,
    removeList,
  } = useListsContext();
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selectedList = lists.find((list) => list.ListId === selectedListId);
  const selectedItems =
    selectedList == null ? [] : itemsByList[selectedList.ListId] ?? [];
  const checkedCount = selectedItems.filter((item) => item.Checked).length;

  // Focus moves into the menu so the arrow keys drive it, and a press anywhere outside
  // the control closes it. pointerdown rather than mousedown so a touch counts at once.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    menuRef.current?.focus({ preventScroll: true });
    const handlePointerDown = (event: PointerEvent) => {
      if (
        anchorRef.current != null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && lists[activeIndex] != null) {
      document
        .getElementById(optionId(lists[activeIndex].ListId))
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [menuOpen, activeIndex, lists]);

  function optionId(listId: string) {
    return `list-option-${listId}`;
  }

  // The open menu is the only place the other lists are visible, so each option
  // carries its own progress.
  function optionProgress(listId: string) {
    const items = itemsByList[listId] ?? [];
    if (items.length === 0) {
      return "Empty";
    }
    const done = items.filter((item) => item.Checked).length;
    return `${done} of ${items.length} checked`;
  }

  function openMenu() {
    const index = lists.findIndex((list) => list.ListId === selectedListId);
    setActiveIndex(index < 0 ? 0 : index);
    setMenuOpen(true);
  }

  function closeMenu() {
    setMenuOpen(false);
    triggerRef.current?.focus();
  }

  function choose(listId: string) {
    selectList(listId);
    closeMenu();
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu();
    }
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    switch (event.key) {
      case "ArrowDown":
        setActiveIndex((index) => Math.min(index + 1, lists.length - 1));
        break;
      case "ArrowUp":
        setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case "Home":
        setActiveIndex(0);
        break;
      case "End":
        setActiveIndex(lists.length - 1);
        break;
      case "Enter":
      case " ":
        if (lists[activeIndex] != null) {
          choose(lists[activeIndex].ListId);
        }
        break;
      case "Escape":
        closeMenu();
        break;
      case "Tab":
        // Let focus move on as normal; the menu just gets out of the way.
        setMenuOpen(false);
        return;
      default:
        return;
    }
    event.preventDefault();
  }

  // Naming the item count makes the cost of confirming concrete.
  function deleteMessage(name: string, itemCount: number) {
    if (itemCount === 0) {
      return `Delete "${name}"? This cannot be undone.`;
    }
    const items = itemCount === 1 ? "item" : "items";
    return `Delete "${name}" and all ${itemCount} of its ${items}? This cannot be undone.`;
  }

  return (
    <SelectRow>
      <SelectAnchor ref={anchorRef}>
        <SelectShell
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
          onKeyDown={handleTriggerKeyDown}
        >
          <SelectedName>{selectedList?.Name}</SelectedName>
          <DoneCount>
            {checkedCount} of {selectedItems.length} checked
          </DoneCount>
          <SelectChevron aria-hidden="true">
            <CaretDownFill />
          </SelectChevron>
        </SelectShell>
        {menuOpen && (
          <ListMenu
            ref={menuRef}
            role="listbox"
            aria-label="Lists"
            tabIndex={-1}
            aria-activedescendant={
              lists[activeIndex] != null
                ? optionId(lists[activeIndex].ListId)
                : undefined
            }
            onKeyDown={handleMenuKeyDown}
          >
            {lists.map((list, index) => {
              const selected = list.ListId === selectedListId;
              return (
                <ListOption
                  key={list.ListId}
                  id={optionId(list.ListId)}
                  role="option"
                  aria-selected={selected}
                  $active={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(list.ListId)}
                >
                  <OptionCheck aria-hidden="true">
                    {selected && <Check2 />}
                  </OptionCheck>
                  <ListOptionText>
                    <ListOptionName $selected={selected}>
                      {list.Name}
                    </ListOptionName>
                    <DoneCount>{optionProgress(list.ListId)}</DoneCount>
                  </ListOptionText>
                </ListOption>
              );
            })}
          </ListMenu>
        )}
      </SelectAnchor>
      {selectedList != null && (
        <>
          {/* title gives the hover tooltip, aria-label the name for screen readers,
              since neither button carries visible text. */}
          <IconButton
            type="button"
            title={`Rename "${selectedList.Name}"`}
            aria-label={`Rename "${selectedList.Name}"`}
            onClick={() => setRenameOpen(true)}
          >
            <Pencil />
          </IconButton>
          <DeleteIconButton
            type="button"
            title={`Delete "${selectedList.Name}"`}
            aria-label={`Delete "${selectedList.Name}"`}
            onClick={() => setDeleteOpen(true)}
          >
            <Trash />
          </DeleteIconButton>
        </>
      )}
      {deleteOpen && selectedList != null && (
        <ConfirmModal
          heading="Delete List"
          message={deleteMessage(selectedList.Name, selectedItems.length)}
          confirmLabel="Delete List"
          onConfirm={() => removeList(selectedList.ListId)}
          onClose={() => setDeleteOpen(false)}
        />
      )}
      {renameOpen && selectedList != null && (
        <ListNameModal
          heading="Rename List"
          submitLabel="Save"
          initialName={selectedList.Name}
          onSubmit={(name) => renameList(selectedList.ListId, name)}
          onClose={() => setRenameOpen(false)}
        />
      )}
    </SelectRow>
  );
}
